/**
 * Group Products Service
 * Manages products within groups using the lv_products table
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type { LVProduct } from '../types';
import { logger } from '@/lib/logger';

class GroupProductsService {
  private async client() {
    return getAuthenticatedSupabaseClient();
  }

  /**
   * Get all products in a group
   */
  async getGroupProducts(groupId: string): Promise<LVProduct[]> {
    // Get products that belong to this group
    const supabase = await this.client();
    const { data, error } = await supabase
      .from('lv_products')
      .select(`
        *,
        product:product_library(*)
      `)
      .eq('parent_group_id', groupId)
      .order('created_at', { ascending: true });

    if (error) {
      logger.error('Error fetching group products:', error);
      throw new Error(`Failed to fetch group products: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Add products to a group by creating lv_products entries
   */
  async addProductsToGroup(groupId: string, productIds: string[]): Promise<void> {
    // First, get the group details to know which floor plan we're on
    const supabase = await this.client();
    const { data: groupData, error: groupError } = await supabase
      .from('lv_products')
      .select('floor_plan_id, x_position, y_position')
      .eq('id', groupId)
      .single();

    if (groupError || !groupData) {
      throw new Error('Failed to fetch group details');
    }

    // Get the product details from library
    const { data: products, error: productsError } = await supabase
      .from('product_library')
      .select('*')
      .in('id', productIds);

    if (productsError || !products) {
      throw new Error('Failed to fetch product details');
    }

    // Count existing products of each type to generate names
    const productCounts = new Map<string, number>();
    
    // Get existing products in the group to count them
    const { data: existingProducts } = await supabase
      .from('lv_products')
      .select('name, product_id')
      .eq('parent_group_id', groupId);

    if (existingProducts) {
      existingProducts.forEach(p => {
        if (p.product_id) {
          const count = productCounts.get(p.product_id) || 0;
          productCounts.set(p.product_id, count + 1);
        }
      });
    }

    // Create lv_products entries for each product
    const lvProducts = productIds.map((productId, index) => {
      const product = products.find(p => p.id === productId);
      if (!product) throw new Error(`Product ${productId} not found`);

      // Get current count for this product type
      const currentCount = productCounts.get(productId) || 0;
      productCounts.set(productId, currentCount + 1);

      // Generate name
      const name = currentCount === 0 
        ? `${product.brand} ${product.model}`
        : `${product.brand} ${product.model} #${currentCount + 1}`;

      return {
        floor_plan_id: groupData.floor_plan_id,
        product_id: productId,
        category: product.category,
        subcategory: product.subcategory,
        name,
        // Position slightly offset from group center for visibility
        x_position: groupData.x_position + (index % 3) * 30,
        y_position: groupData.y_position + Math.floor(index / 3) * 30,
        parent_group_id: groupId,
      };
    });

    const { error: insertError } = await supabase
      .from('lv_products')
      .insert(lvProducts);

    if (insertError) {
      logger.error('Error adding products to group:', insertError);
      throw new Error(`Failed to add products to group: ${insertError.message}`);
    }
  }

  /**
   * Remove a product from a group
   */
  async removeProductFromGroup(groupId: string, productId: string): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase
      .from('lv_products')
      .delete()
      .eq('id', productId)
      .eq('parent_group_id', groupId);

    if (error) {
      logger.error('Error removing product from group:', error);
      throw new Error(`Failed to remove product from group: ${error.message}`);
    }
  }

  /**
   * Update product name
   */
  async updateProductName(productId: string, name: string): Promise<void> {
    const supabase = await this.client();
    const { error } = await supabase
      .from('lv_products')
      .update({ name })
      .eq('id', productId);

    if (error) {
      logger.error('Error updating product name:', error);
      throw new Error(`Failed to update product name: ${error.message}`);
    }
  }
}

export const groupProductsService = new GroupProductsService();
